import React, { useEffect, useState } from 'react';
import { Megaphone, X, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ORBIT WEBSITE LINK
const ORBIT_WEBSITE_LINK = 'https://orbit.aiec-iitm.in/';

const notifications = [];

const QuizNotifier = () => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setTimeout(() => {
        if (!mounted) return;
        setVisible(true);
      }, 600);
    };

    init();
    return () => { mounted = false; };
  }, []);

  // Cycle notifications one by one every 4.5 seconds
  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % notifications.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [visible]);

  const handleAction = (item) => {
    if (item.isExternal && item.link && item.link !== '#' && item.link !== 'https://your-orbit-website-link.com') {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback smooth scroll to events section on home page
      window.history.pushState({}, '', '/events');
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.dispatchEvent(new CustomEvent('navigateToSection', { detail: { id: 'events' } }));
    }
  };

  const dismiss = () => {
    sessionStorage.setItem('orbit_notifier_dismissed', '1');
    setVisible(false);
  };

  if (!visible || notifications.length === 0) return null;

  const currentNotif = notifications[currentIndex];

  return (
    <div className="quiz-notifier">
      <div className="qn-inner">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentNotif.id}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="qn-content-row"
          >
            <div className="qn-left">
              <div className="qn-icon">
                {currentNotif.icon}
              </div>
              <div className="qn-text">
                <div className="qn-header-row">
                  <span className="qn-title">{currentNotif.title}</span>
                  <span className="qn-badge">{currentNotif.badge}</span>
                </div>
                <div className="qn-sub">
                  <Calendar size={13} />
                  <span>{currentNotif.subtitle}</span>
                </div>
              </div>
            </div>
            <div className="qn-actions">
              <button className="qn-primary" onClick={() => handleAction(currentNotif)}>
                {currentNotif.buttonText} <ArrowRight size={15} />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="qn-top-controls">
          <div className="qn-dots">
            {notifications.map((n, idx) => (
              <span
                key={n.id}
                className={`qn-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(idx)}
              />
            ))}
          </div>
          <button className="qn-close" onClick={dismiss} aria-label="Dismiss">
            <X size={15} />
          </button>
        </div>
      </div>

      <style jsx>{`
        .quiz-notifier {
          position: fixed;
          right: 20px;
          top: calc(var(--header-height, 68px) + 12px);
          z-index: 4000;
        }
        .qn-inner {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 6px;
          background: rgba(18, 18, 18, 0.88);
          border: 1px solid rgba(255, 215, 0, 0.35);
          border-radius: 16px;
          padding: 12px 16px;
          backdrop-filter: blur(16px);
          box-shadow: 
            0 10px 40px rgba(0, 0, 0, 0.6), 
            0 0 22px rgba(212, 175, 55, 0.18);
          color: #fff;
          min-width: 360px;
          max-width: 90vw;
          animation: slideInNotifier 0.6s cubic-bezier(0.23, 1, 0.32, 1);
          overflow: hidden;
        }
        @keyframes slideInNotifier {
          from { opacity: 0; transform: translateX(30px) scale(0.95); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .qn-content-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
        }
        .qn-left { display: flex; align-items: center; gap: 12px; flex: 1; }
        .qn-icon {
          width: 38px; height: 38px; border-radius: 12px;
          display: grid; place-items: center;
          background: linear-gradient(135deg, #D4AF37, #FFD700);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
          color: #000;
          flex-shrink: 0;
        }
        .qn-text { display: flex; flex-direction: column; gap: 2px; }
        .qn-header-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .qn-title { 
          font-weight: 800; 
          font-size: 0.98rem; 
          background: linear-gradient(135deg, #FFD700, #FDB931); 
          -webkit-background-clip: text; 
          -webkit-text-fill-color: transparent; 
          letter-spacing: 0.3px;
        }
        .qn-badge {
          font-size: 0.65rem;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 6px;
          background: rgba(255, 215, 0, 0.15);
          color: #FFD700;
          border: 1px solid rgba(255, 215, 0, 0.3);
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .qn-sub { display: flex; align-items: center; gap: 5px; color: rgba(255, 255, 255, 0.75); font-size: 0.78rem; font-weight: 500; }
        .qn-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; flex-shrink: 0; }
        .qn-primary { 
          display: inline-flex; 
          align-items: center; 
          gap: 6px; 
          padding: 7px 13px; 
          border-radius: 10px; 
          border: 1px solid rgba(255, 215, 0, 0.4); 
          cursor: pointer; 
          color: #000; 
          background: linear-gradient(135deg, #FFD700, #D4AF37); 
          box-shadow: 0 4px 15px rgba(212, 175, 55, 0.25); 
          font-weight: 700; 
          font-size: 0.82rem;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .qn-primary:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4); 
          filter: brightness(1.1);
        }
        .qn-top-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding-top: 4px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          margin-top: 2px;
        }
        .qn-dots { display: flex; align-items: center; gap: 5px; }
        .qn-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .qn-dot.active {
          width: 16px;
          border-radius: 4px;
          background: #FFD700;
          box-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
        }
        .qn-close { background: transparent; border: 0; color: rgba(255, 255, 255, 0.5); display: grid; place-items: center; padding: 3px; border-radius: 6px; cursor: pointer; transition: all 0.2s; margin-left: auto; }
        .qn-close:hover { color: #FFD700; background: rgba(255, 215, 0, 0.15); }
        @media (max-width: 480px) {
          .quiz-notifier { right: 12px; top: calc(var(--header-height, 68px) + 8px); }
          .qn-inner { padding: 10px 12px; min-width: unset; max-width: 92vw; gap: 6px; border-radius: 14px; }
          .qn-icon { width: 32px; height: 32px; border-radius: 10px; }
          .qn-title { font-size: 0.88rem; }
          .qn-badge { font-size: 0.6rem; padding: 1px 4px; }
          .qn-sub { font-size: 0.72rem; }
          .qn-primary { padding: 5px 10px; border-radius: 8px; font-size: 0.75rem; }
        }
      `}</style>
    </div>
  );
};

export default QuizNotifier;

